import { FC, useCallback, useEffect, useMemo } from 'react';
import { useAsync } from 'react-use';
import { GenericOption } from 'pages/AutomationEngine/types';
import TeamMemberService from 'services/TeamMemberService';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';
import type { IntegrationSecret } from '@kizen/api/integration-secrets';
import MultiSelect from '__components/Inputs/MultiSelect';
import { useTranslation } from 'react-i18next';
import ClearSelectButton from '__components/Inputs/Select/ClearButton';
import { useFlashTransition } from 'hooks/useFlashState';

type SecretMultiSelectProps = {
  value: string[];
  onChange: (value: GenericOption[]) => void;
  error?: string[];
};

const EMPTY_ARRAY: GenericOption[] = [];
const EMPTY_OBJECT: Record<string, GenericOption> = {};
const EMPTY_VALUE: string[] = [];

export const SecretsMultiSelect: FC<SecretMultiSelectProps> = ({
  value = EMPTY_VALUE,
  onChange,
  error,
}) => {
  const { t } = useTranslation();
  const {
    loading,
    value: { options = EMPTY_ARRAY, lookup = EMPTY_OBJECT } = {},
  } = useAsync(async () => {
    const { results }: { results: IntegrationSecret[] } =
      await TeamMemberService.getIntegrationSecrets({
        page: 1,
        page_size: FORCE_ALL_RECORDS_SIZE,
        search: '',
        ordering: 'description',
      });

    const lookup = results.reduce<Record<string, GenericOption>>(
      (acc, item: IntegrationSecret) =>
        item.api_name
          ? {
              ...acc,
              [item.api_name]: {
                value: item.api_name,
                label: `${item.description} (${item.api_name})`,
              },
            }
          : acc,
      {}
    );
    const options = results.map((item: IntegrationSecret) => ({
      label: `${item.description} (${item.api_name})`,
      value: item.api_name,
    }));

    return { options, lookup };
  }, []);

  const secretsValue = useMemo(() => {
    return value.map((item, i) => ({
      ...(lookup[item] || {
        label: item,
        value: item,
      }),
    }));
  }, [lookup, value]);

  const [message, showMessage, flashError, cancelFlash] = useFlashTransition();

  useEffect(() => {
    if (error) {
      flashError(error);
    }
  }, [error, flashError]);

  const handleChange = useCallback(
    (value: GenericOption[]) => {
      onChange(value);
      cancelFlash();
    },
    [onChange, cancelFlash]
  );

  const validate = useMemo(
    () => ({ message, showMessage }),
    [message, showMessage]
  );

  return (
    <MultiSelect
      value={secretsValue}
      options={options}
      isLoading={loading}
      onChange={handleChange}
      label={t('Integration Secrets')}
      placeholder={t('Find Integration Secrets')}
      menuLeftButton={<ClearSelectButton />}
      margin
      inModal
      validate={validate}
      error={Boolean(error)}
    />
  );
};
